//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { Box, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Typography, useTheme } from '@mui/material'
import React, { ReactElement, useEffect } from 'react'

import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import CloseIcon from '@mui/icons-material/Close'
import TextSnippetOutlinedIcon from '@mui/icons-material/TextSnippetOutlined'
import IconButton from '@mui/material/IconButton'
import Modal from '@mui/material/Modal'
import Paper from '@mui/material/Paper'
import { DocumentInfo, listDocuments, urlForPdfFile } from '@react/apx/AwsApx'
import { EthAddr } from '@shared/SchemaTypes'

import { useOtChainState } from '@react/redux/store'

const modalStyleFullPage = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 800,
  bgcolor: 'background.paper',
  borderRadius: '20px',
  boxShadow: '0px 16px 50px rgba(48, 49, 51, 0.08), 0px 0px 0.5px #303133',
  p: 4,
}

export default function ViewDocumentList(props: {
  poolAddr: EthAddr
  setHasRequiredDocs?: (value: boolean) => void
  refreshNumber: number
}): ReactElement {
  const [fileList, setFileList] = React.useState<DocumentInfo[]>([])
  const { poolAddr } = props
  const [openModal, setOpenModal] = React.useState(false)
  const [pdfViewName, setPdfViewName] = React.useState<string | undefined>(undefined)

  const {
    stageConfiguration: { stageName },
    chainConfiguration: {
      chainId,

      perimeterAddresses,
    },
  } = useOtChainState()

  const theme = useTheme()
  useEffect(() => {
    if (poolAddr && stageName) {
      const asyncFunction = async (): Promise<void> => {
        const fl = await listDocuments(stageName, poolAddr)
        if (fl.length > 0 && props.setHasRequiredDocs) {
          props.setHasRequiredDocs(true)
        } else if (props.setHasRequiredDocs) {
          props.setHasRequiredDocs(false)
        }
        setFileList(fl)
      }
      asyncFunction().catch((e) => {
        console.error(e)
      })
    }
  }, [props.refreshNumber, poolAddr, chainId, perimeterAddresses])

  const handlePDFOpen = (fileName: string): void => {
    console.log('handlePDFOpen', fileName)
    setPdfViewName(fileName)
    setOpenModal(true)
  }
  const pdfFileName = pdfViewName?.split('/').pop() ?? ''
  return (
    <React.Fragment>
      <Modal
        open={openModal}
        onClose={() => setOpenModal(false)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Paper sx={modalStyleFullPage}>
          <Typography align="left" sx={{ display: 'inline', float: 'left', fontWeight: 'bold' }} variant={'h4'}>
            {pdfFileName}{' '}
          </Typography>
          <IconButton
            onClick={() => setOpenModal(false)}
            sx={{
              display: 'inline',
              height: '14px',
              width: '14px',
              padding: 0,
              float: 'right',
              color: theme.palette.primary.main,
            }}
          >
            <CloseIcon
              sx={{
                width: '12px',
                height: '12px',
                marginTop: 0,
                color: theme.palette.fg.muted,
              }}
            />
          </IconButton>

          <Box sx={{ marginTop: '32px' }}>
            {pdfViewName && (
              <iframe style={{ height: 900, width: '100%' }} src={urlForPdfFile(stageName, pdfViewName)} />
            )}
          </Box>
        </Paper>
      </Modal>

      <List>
        {fileList.map((fileInfo) => {
          return (
            <ListItem
              key={fileInfo.fileName}
              disablePadding
              onClick={() => {
                handlePDFOpen(fileInfo.fileName)
              }}
              secondaryAction={
                <IconButton
                  edge="end"
                  aria-label="open"
                  onClick={() => {
                    handlePDFOpen(fileInfo.fileName)
                  }}
                >
                  <ArrowForwardIosIcon />
                </IconButton>
              }
            >
              <ListItemButton>
                <ListItemIcon>
                  <TextSnippetOutlinedIcon
                    viewBox={'0 0 25,20'}
                    preserveAspectRatio="none"
                    sx={{
                      maxWidth: '20px',
                      height: '25px',
                      color: theme.palette.accent1.main,
                    }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={fileInfo.fileName.substring(fileInfo.fileName.lastIndexOf('/') + 1)}
                  secondary={new Date(fileInfo.modifiedDate).toLocaleDateString()}
                />
              </ListItemButton>
            </ListItem>
          )
        })}
      </List>
    </React.Fragment>
  )
}
